// src/components/PlotManagement.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { collection, query, orderBy, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";

const VILLA_COLLECTION = "ClarksExoticaVilla";

const safeNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v.toString().replace(/,/g, "")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatNum = (num) => {
  try {
    return new Intl.NumberFormat("en-IN").format(safeNum(num));
  } catch {
    return safeNum(num).toLocaleString();
  }
};

const safeString = (value) => String(value || "").toLowerCase();

const extractVillaIndex = (villaIdOrNo) => {
  // villa-12 / Villa 12 / 12 -> 12
  const s = String(villaIdOrNo || "");
  const m = s.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const PlotManagement = ({ setError, refreshPlots }) => {
  const [villas, setVillas] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [villasError, setVillasError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  const [selectedVilla, setSelectedVilla] = useState(null);

  const [statusModalVilla, setStatusModalVilla] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [editingVilla, setEditingVilla] = useState(null);

  // ✅ saving + feedback in edit modal
  const [isSaving, setIsSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");
  const [editMsgType, setEditMsgType] = useState(""); // "success" | "error"

  const fetchVillas = useCallback(async () => {
    setIsLoading(true);
    setVillasError(null);

    try {
      const ref = collection(db, VILLA_COLLECTION);
      const q = query(ref, orderBy("villaId", "asc"));
      const snap = await getDocs(q);

      const data = snap.docs.map((d) => {
        const v = d.data();
        const idx = extractVillaIndex(v.villaNumber) ?? extractVillaIndex(v.villaId) ?? null;

        return {
          firestoreId: d.id,
          numericId: idx,

          villaId: v.villaId || "villa-?",
          villaNumber: v.villaNumber || "N/A",

          configuration: v.configuration || "Unknown",
          facing: v.facing || "Unknown",

          builtUpAreaSqft: v.builtUpAreaSqft ?? "TBD",
          LandAreaSqft: v.LandAreaSqft ?? "TBD",

          premiumCharges: v.premiumCharges ?? "TBD",
          inclusivePrice: v.inclusivePrice ?? "TBD",

          Status: v.Status || "Available",
        };
      });

      const sorted = [...data].sort((a, b) => {
        if (a.numericId != null && b.numericId != null) return a.numericId - b.numericId;
        return String(a.villaId || "").localeCompare(String(b.villaId || ""));
      });

      if (!sorted.length) setVillasError("No villas found in inventory.");
      setVillas(sorted);
      setFiltered(sorted);
    } catch (err) {
      console.error("Error fetching villas:", err);
      setError?.("Failed to fetch villa inventory.");
      setVillasError("Failed to fetch villa inventory.");
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchVillas();
  }, [fetchVillas]);

  // Search
  const handleSearch = () => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setFiltered(villas);
      setVillasError(null);
      return;
    }

    const numericQuery = q.replace(/[^0-9]/g, "");

    const out = villas.filter((v) => {
      const villaIdStr = safeString(v.villaId);
      const villaNoStr = safeString(v.villaNumber);
      const configStr = safeString(v.configuration);
      const facingStr = safeString(v.facing);
      const statusStr = safeString(v.Status);

      const builtUpStr = safeString(v.builtUpAreaSqft);
      const landStr = safeString(v.LandAreaSqft);

      let numericMatch = false;
      if (numericQuery) {
        if (v.numericId != null && String(v.numericId) === numericQuery) numericMatch = true;
        if (villaIdStr.includes(numericQuery) || villaNoStr.includes(numericQuery)) numericMatch = true;
      }

      return (
        villaIdStr.includes(q) ||
        villaNoStr.includes(q) ||
        configStr.includes(q) ||
        facingStr.includes(q) ||
        statusStr.includes(q) ||
        builtUpStr.includes(q) ||
        landStr.includes(q) ||
        numericMatch
      );
    });

    if (!out.length) {
      setVillasError(
        `No villas found for "${searchQuery}". Try villa number, villaId, configuration, facing, or status.`
      );
    } else {
      setVillasError(null);
    }

    setFiltered(out);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFiltered(villas);
    setVillasError(null);
  };

  // Modals
  const openDetails = (villa) => setSelectedVilla(villa);
  const closeDetails = () => setSelectedVilla(null);

  const openStatusModal = (villa) => {
    setStatusModalVilla(villa);
    setSelectedStatus(villa.Status || "Available");
  };
  const closeStatusModal = () => {
    setStatusModalVilla(null);
    setSelectedStatus("");
  };

  const openConfirmModal = () => setShowConfirmModal(true);
  const closeConfirmModal = () => setShowConfirmModal(false);

  const handleStatusChange = async () => {
    if (!statusModalVilla || !selectedStatus) return;

    try {
      const ref = doc(db, VILLA_COLLECTION, statusModalVilla.firestoreId);
      await updateDoc(ref, { Status: selectedStatus });

      const apply = (prev) =>
        prev.map((x) =>
          x.firestoreId === statusModalVilla.firestoreId ? { ...x, Status: selectedStatus } : x
        );

      setVillas(apply);
      setFiltered(apply);

      if (selectedVilla?.firestoreId === statusModalVilla.firestoreId) {
        setSelectedVilla((p) => ({ ...p, Status: selectedStatus }));
      }

      refreshPlots?.();
      closeConfirmModal();
      closeStatusModal();
      setError?.("Status updated successfully!");
    } catch (err) {
      console.error("Error updating villa status:", err);
      setError?.("Failed to update villa status.");
    }
  };

  const openEditModal = (villa) => {
    setEditMsg("");
    setEditMsgType("");

    setEditingVilla({
      ...villa,
      builtUpAreaSqft: villa.builtUpAreaSqft === "TBD" ? "" : villa.builtUpAreaSqft,
      LandAreaSqft: villa.LandAreaSqft === "TBD" ? "" : villa.LandAreaSqft,
      premiumCharges: villa.premiumCharges === "TBD" ? "" : villa.premiumCharges,
      inclusivePrice: villa.inclusivePrice === "TBD" ? "" : villa.inclusivePrice,
      configuration: villa.configuration || "",
      facing: villa.facing || "",
    });
  };

  const closeEditModal = () => {
    setEditingVilla(null);
    setIsSaving(false);
    setEditMsg("");
    setEditMsgType("");
  };

  const updateVillaField = (field, value) => {
    setEditingVilla((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveVillaUpdates = async () => {
    if (!editingVilla) return;

    setIsSaving(true);
    setEditMsg("");
    setEditMsgType("");

    try {
      const ref = doc(db, VILLA_COLLECTION, editingVilla.firestoreId);

      const updates = {
        configuration: (editingVilla.configuration || "Unknown").trim() || "Unknown",
        facing: (editingVilla.facing || "Unknown").trim() || "Unknown",

        builtUpAreaSqft:
          editingVilla.builtUpAreaSqft === "" ? "TBD" : safeNum(editingVilla.builtUpAreaSqft),

        LandAreaSqft:
          editingVilla.LandAreaSqft === "" ? "TBD" : safeNum(editingVilla.LandAreaSqft),

        premiumCharges:
          editingVilla.premiumCharges === "" ? "TBD" : safeNum(editingVilla.premiumCharges),

        inclusivePrice:
          editingVilla.inclusivePrice === "" ? "TBD" : safeNum(editingVilla.inclusivePrice),
      };

      console.log("Updating villa:", editingVilla.firestoreId, updates);

      await updateDoc(ref, updates);

      const updated = { ...editingVilla, ...updates };
      const apply = (prev) => prev.map((x) => (x.firestoreId === editingVilla.firestoreId ? updated : x));

      setVillas(apply);
      setFiltered(apply);

      if (selectedVilla?.firestoreId === editingVilla.firestoreId) {
        setSelectedVilla(updated);
      }

      refreshPlots?.();

      setEditMsgType("success");
      setEditMsg("Saved successfully ✅");
      setError?.("Villa updated successfully!");

      setTimeout(() => closeEditModal(), 800);
    } catch (err) {
      console.error("Error updating villa:", err);

      const code = err?.code ? `(${err.code}) ` : "";
      const msg = err?.message || "Unknown error";

      setEditMsgType("error");
      setEditMsg(`Update failed ${code}- ${msg}`);
      setError?.(`Failed to update villa details ${code}`);
    } finally {
      setIsSaving(false);
    }
  };

  const titleText = useMemo(() => "Villa Inventory", []);

  return (
    <div className="md-section-content">
      <div className="md-section-header">
        <h2 className="md-section-title">{titleText}</h2>

        <button className="md-close-tab-button" onClick={fetchVillas}>
          Refresh
        </button>
      </div>

      <div className="md-search-container">
        <input
          type="text"
          placeholder='Search: "Villa 12", "villa-12", "3BHK", "East", "Sold"...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="md-search-input"
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button className="md-search-button" onClick={handleSearch}>
          Search
        </button>
        <button className="md-clear-button" onClick={clearSearch}>
          Clear
        </button>
      </div>

      {isLoading ? (
        <div className="md-loading">Loading villas...</div>
      ) : villasError ? (
        <div className="md-error">{villasError}</div>
      ) : filtered.length === 0 ? (
        <p className="md-no-results">No villas found.</p>
      ) : (
        <div className="md-users-table-container md-scrollable-table">
          <table className="md-users-table">
            <thead>
              <tr>
                <th>Villa No</th>
                <th>Villa ID</th>
                <th>Config</th>
                <th>Facing</th>
                <th>Built-up (sqft)</th>
                <th>Land (sqft)</th>
                <th>Inclusive Price</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((v) => (
                <tr key={v.firestoreId} className="md-user-row" onClick={() => openDetails(v)}>
                  <td>{v.villaNumber}</td>
                  <td>{v.villaId}</td>
                  <td>{v.configuration}</td>
                  <td>{v.facing}</td>
                  <td>{v.builtUpAreaSqft === "TBD" ? "TBD" : formatNum(v.builtUpAreaSqft)}</td>
                  <td>{v.LandAreaSqft === "TBD" ? "TBD" : formatNum(v.LandAreaSqft)}</td>
                  <td>{v.inclusivePrice === "TBD" ? "TBD" : `₹ ${formatNum(v.inclusivePrice)}`}</td>
                  <td>
                    <span className={`md-booking-status ${safeString(v.Status)}`}>
                      {v.Status || "N/A"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===================== Details Modal ===================== */}
      {selectedVilla && (
        <div
          className="md-modal-overlay"
          onMouseDown={(e) => e.target.classList.contains("md-modal-overlay") && closeDetails()}
        >
          <div className="md-modal-content md-plot-details-modal">
            <div className="md-plot-header">
              <h3 className="md-modal-title">
                {selectedVilla.villaNumber} • {selectedVilla.villaId}
              </h3>
            </div>

            <div className="md-plot-content">
              <div className="md-plot-info">
                <h4 className="md-info-title">Villa Information</h4>

                <p className="md-plot-detail">
                  <span>Configuration:</span> {selectedVilla.configuration}
                </p>
                <p className="md-plot-detail">
                  <span>Facing:</span> {selectedVilla.facing}
                </p>

                <p className="md-plot-detail">
                  <span>Built-up:</span>{" "}
                  {selectedVilla.builtUpAreaSqft === "TBD"
                    ? "TBD"
                    : `${formatNum(selectedVilla.builtUpAreaSqft)} sqft`}
                </p>

                <p className="md-plot-detail">
                  <span>Land Area:</span>{" "}
                  {selectedVilla.LandAreaSqft === "TBD"
                    ? "TBD"
                    : `${formatNum(selectedVilla.LandAreaSqft)} sqft`}
                </p>

                <p className="md-plot-detail">
                  <span>Premium Charges:</span>{" "}
                  {selectedVilla.premiumCharges === "TBD"
                    ? "TBD"
                    : `₹ ${formatNum(selectedVilla.premiumCharges)}`}
                </p>

                <p className="md-plot-detail">
                  <span>Inclusive Price:</span>{" "}
                  {selectedVilla.inclusivePrice === "TBD"
                    ? "TBD"
                    : `₹ ${formatNum(selectedVilla.inclusivePrice)}`}
                </p>
              </div>

              <div className="md-tree-info">
                <div className="md-tree-column">
                  <h4 className="md-info-title">Status</h4>
                  <div className="md-status-info">
                    <span className={`md-booking-status ${safeString(selectedVilla.Status)}`}>
                      {selectedVilla.Status || "Unknown"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="md-plot-footer">
              <div className="md-plot-actions">
                <button className="md-change-button" onClick={() => openStatusModal(selectedVilla)}>
                  Change Status
                </button>
                <button className="md-edit-trees-button" onClick={() => openEditModal(selectedVilla)}>
                  Edit Details
                </button>
              </div>
            </div>

            <div className="md-modal-actions">
              <button className="md-modal-button md-cancel" onClick={closeDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Status Change Modal ===================== */}
      {statusModalVilla && !showConfirmModal && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Change Status • {statusModalVilla.villaNumber}</h3>

            <div className="md-modal-section">
              <label className="md-status-label">Select Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="md-status-select"
              >
                <option value="Available">Available</option>
                <option value="Booked">Booked</option>
                <option value="Sold">Sold</option>
                <option value="Soft-Block">Soft-Block</option>
              </select>
            </div>

            <div className="md-modal-actions">
              <button className="md-modal-button md-save" onClick={openConfirmModal}>
                Save
              </button>
              <button className="md-modal-button md-cancel" onClick={closeStatusModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Confirmation Modal ===================== */}
      {showConfirmModal && statusModalVilla && (
        <div className="md-modal-overlay">
          <div className="md-modal-content">
            <h3 className="md-modal-titles">Confirm Status Change</h3>

            <p className="md-modal-text">
              Change status to <strong>"{selectedStatus}"</strong> for{" "}
              <strong>{statusModalVilla.villaNumber}</strong>?
            </p>

            <div className="md-modal-actions">
              <button className="md-modal-button md-save" onClick={handleStatusChange}>
                Yes
              </button>
              <button className="md-modal-button md-cancel" onClick={closeConfirmModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Edit Modal ===================== */}
      {editingVilla && (
        <div className="md-modal-overlay">
          <div className="md-modal-content md-edit-modal">
            <h3 className="md-modal-title">Edit • {editingVilla.villaNumber}</h3>

            <div className="md-modal-section">
              <div className="md-form-grid">
                <div className="md-form-field">
                  <label>Configuration</label>
                  <input
                    type="text"
                    value={editingVilla.configuration}
                    onChange={(e) => updateVillaField("configuration", e.target.value)}
                  />
                </div>

                <div className="md-form-field">
                  <label>Facing</label>
                  <input
                    type="text"
                    value={editingVilla.facing}
                    onChange={(e) => updateVillaField("facing", e.target.value)}
                  />
                </div>

                <div className="md-form-field">
                  <label>Built-up Area (sqft)</label>
                  <input
                    type="number"
                    min="0"
                    value={editingVilla.builtUpAreaSqft}
                    onChange={(e) => updateVillaField("builtUpAreaSqft", e.target.value)}
                  />
                </div>

                <div className="md-form-field">
                  <label>Land Area (sqft)</label>
                  <input
                    type="number"
                    min="0"
                    value={editingVilla.LandAreaSqft}
                    onChange={(e) => updateVillaField("LandAreaSqft", e.target.value)}
                  />
                </div>

                <div className="md-form-field">
                  <label>Premium Charges</label>
                  <input
                    type="number"
                    min="0"
                    value={editingVilla.premiumCharges}
                    onChange={(e) => updateVillaField("premiumCharges", e.target.value)}
                  />
                </div>

                <div className="md-form-field">
                  <label>Inclusive Price</label>
                  <input
                    type="number"
                    min="0"
                    value={editingVilla.inclusivePrice}
                    onChange={(e) => updateVillaField("inclusivePrice", e.target.value)}
                  />
                </div>
              </div>

              {editMsg && (
                <div
                  className={`md-edit-msg ${editMsgType}`}
                  style={{
                    marginTop: 12,
                    padding: "10px",
                    borderRadius: 8,
                    fontSize: 14,
                    border: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  {editMsg}
                </div>
              )}
            </div>

            <div className="md-modal-actions">
              <button
                className="md-modal-button md-save"
                onClick={saveVillaUpdates}
                disabled={isSaving}
                style={{
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>

              <button
                className="md-modal-button md-cancel"
                onClick={closeEditModal}
                disabled={isSaving}
                style={{
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotManagement;
